import { useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Modal from '@mui/material/Modal'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import Loading from 'components/Loading'
import LoadingFullPageWithOverlay from 'components/LoadingFullPageWithOverlay'
import { useGlobalState } from 'contexts/global-state'
import { ApiService } from 'services/api'
import type { StudentWithoutDocument } from 'services/api/students'

type Props = {
  opened: boolean
  activityId: string
  onClose: () => void
}

export function StudentsWithoutDocumentModal({
  opened,
  activityId,
  onClose,
}: Props) {
  const { openSuccessToast, openErrorToast } = useGlobalState()
  const [isSending, setIsSending] = useState(false)

  const {
    data: students,
    isLoading,
    refetch,
  } = useQuery<StudentWithoutDocument[]>({
    queryKey: ['students-without-document', activityId],
    queryFn: () =>
      ApiService.Students.listWithoutDocumentByActivity(activityId),
    enabled: opened,
  })

  const handleSendReminder = async () => {
    setIsSending(true)
    try {
      await ApiService.Students.notifyMissingDocumentByActivity(activityId)
      openSuccessToast({ message: 'Lembrete enviado com sucesso.' })
      refetch()
    } catch (error) {
      openErrorToast({
        message: (error as ApiError)?.message || 'Erro ao enviar Lembrete.',
      })
    } finally {
      setIsSending(false)
    }
  }

  return (
    <>
      {isSending && <LoadingFullPageWithOverlay />}
      <Modal
        open={opened}
        onClose={onClose}
        aria-labelledby="students-without-document-title"
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 900,
            maxHeight: '80vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: '8px',
            p: 4,
          }}
        >
          <Typography
            id="students-without-document-title"
            variant="h6"
            component="h2"
            mb={2}
          >
            Alunos com documento pendente
          </Typography>

          {isLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <Loading />
            </Box>
          )}

          {!isLoading && (!students || students.length === 0) && (
            <Typography>Nenhum aluno com documento pendente.</Typography>
          )}

          {!isLoading && students && students.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Aluno</TableCell>
                  <TableCell>Data Nasc.</TableCell>
                  <TableCell>Responsável</TableCell>
                  <TableCell>Contato</TableCell>
                  <TableCell>Lembrete enviado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student.id}>
                    <TableCell>{student.fantasy_name}</TableCell>
                    <TableCell>{student.birth_date}</TableCell>
                    <TableCell>{student.parentClients?.fantasy_name}</TableCell>
                    <TableCell>
                      {student.parentClients?.cell_phone ||
                        student.parentClients?.phone || (
                          <em style={{ color: '#999' }}>—</em>
                        )}
                    </TableCell>
                    <TableCell>
                      {student.reservationsChild?.[0]
                        ?.document_required_mail_sent_at ?? (
                        <em style={{ color: '#999' }}>Não enviado</em>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button variant="outlined" color="primary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isSending || isLoading || !students?.length}
              onClick={handleSendReminder}
              style={{ marginLeft: '10px' }}
            >
              Enviar lembrete (Whatsapp)
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
